import { cn } from "~/lib/utils";
import { DataGridCellValue } from "./DataGridCellValue";
import {
    AccessSegment,
    ColumnColors,
    ColumnMetadata,
    ColumnTypes,
    RowSize,
    RowSizes,
} from "./types";
import { AccessGate, getAccessGate } from "./access";
import React, { useEffect, useRef, useState, type JSX } from "react";
import styles from "./styles.module.css";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";

// // // //

function getCellColor<RowType, SortableColumns>(
    column: ColumnMetadata<RowType, SortableColumns>,
    row: RowType,
): ColumnColors {
    if (!column.cellColor) {
        return ColumnColors.None;
    }

    if (typeof column.cellColor === "function") {
        const color = column.cellColor(row);
        if (color === null) {
            return ColumnColors.None;
        }
        return color;
    }

    return column.cellColor as ColumnColors;
}

// // // //

interface Props<RowType, SortableColumns> {
    row: RowType;
    size: RowSize;
    addBoxShadow?: boolean;
    column: ColumnMetadata<RowType, SortableColumns>;
    accessSegment?: AccessSegment;
}

function DataGridCellContent<RowType, SortableColumns>(
    props: Props<RowType, SortableColumns>,
) {
    const {
        column,
        row,
        size,
        addBoxShadow = false,
        accessSegment = AccessSegment.Free,
    } = props;
    const { sticky = null } = column;
    const isStickyLeft = sticky !== null;

    // // // //
    // Check if the cell's content is registering a CSS overflow
    const textOverflowRef = useRef<HTMLDivElement>(null);
    const [ellipsisActive, setEllipsisActive] = useState(false);

    useEffect(() => {
        // Exit if the component hasn't rendered for some reason
        if (!textOverflowRef.current) {
            return;
        }

        // Get the current HTMLDivElement from ref
        const el = textOverflowRef.current;

        // Get the current HTMLDivElement from ref
        let overflowDetected = el.offsetWidth < el.scrollWidth;

        // If the cell's top-level <div> isn't registering a CSS overflow, check for
        // the presence of an element with the `.dg-overflow` CSS class name
        if (!overflowDetected) {
            // Query all child elements with target class name
            const childEls = el.querySelectorAll(".dg-overflow");

            // Check if any child element is registering a CSS overflow
            childEls.forEach((childEl) => {
                // @ts-ignore
                if (childEl.offsetWidth < childEl.scrollWidth) {
                    overflowDetected = true;
                }
            });
        }

        // Set ellipsisIsActive true
        // NOTE - we include check here to prevent redundant re-renders
        if (overflowDetected) {
            setEllipsisActive(overflowDetected);
        }
    }, [textOverflowRef]);
    //
    // // // //

    const isNumeric =
        column.type === ColumnTypes.number ||
        column.type === ColumnTypes.percent ||
        column.type === ColumnTypes.percentChange ||
        column.type === ColumnTypes.dollar ||
        column.type === ColumnTypes.token ||
        column.type === ColumnTypes.years ||
        column.type === ColumnTypes.date ||
        column.type === ColumnTypes.datetime ||
        column.type === ColumnTypes.datetimeCompact ||
        column.type === ColumnTypes.timeAgo ||
        column.type === ColumnTypes.sparkline;

    // Flag whether or not to show the column
    const accessGate = getAccessGate(accessSegment, column.access?.view);
    const isContentLoginGated = accessGate === AccessGate.Login;
    const isContentProGated = accessGate === AccessGate.Upgrade;
    const blurValue = isContentLoginGated || isContentProGated || column.blur;

    // Get color from ColumnMetadata.cellColor
    const color = getCellColor(column, row);
    const [showLoginGatePopover, setShowLoginGatePopover] = useState(false);

    const renderCell = (
        cellProps?: React.HTMLAttributes<HTMLTableCellElement>,
    ) => (
        <td
            scope="col"
            key={column.slug}
            style={{
                width: `${column.width}px`,
                minWidth: `${column.width}px`,
                maxWidth:
                    column.maxWidth !== undefined
                        ? `${column.maxWidth}px`
                        : undefined,
                left: isStickyLeft ? `${sticky.left}px` : undefined,
            }}
            className={cn("flex grow whitespace-nowrap group/cell bg-inherit", {
                "lg:sticky z-30 mt-px": isStickyLeft,
                "hover:z-10": ellipsisActive && !isStickyLeft,
                // "hover:z-30": ellipsisActive,
                "pointer-events-none": blurValue && !isContentLoginGated,
                [styles["unset-shadow"]]: addBoxShadow && ellipsisActive,
                [styles["sticky-left-shadow"]]: addBoxShadow,
                // // // //
                // Handle ColumnMetadata.cellColor
                // "bg-inherit text-foreground": color === ColumnColors.None,
                // "bg-red-50 text-red-800": color === ColumnColors.Red,
                // "bg-amber-50 text-amber-800": color === ColumnColors.Orange,
                // "bg-green-50 text-green-800": color === ColumnColors.Green,
                // "bg-blue-50 text-blue-800": color === ColumnColors.Blue,
                // "bg-yellow-50 text-yellow-800": color === ColumnColors.Yellow,
                // "bg-indigo-50 text-indigo-800": color === ColumnColors.Indigo,
                // "bg-purple-50 text-purple-800": color === ColumnColors.Purple,
                // "bg-pink-50 text-pink-800": color === ColumnColors.Pink,

                // NOTE - We'll start with just text color for now, we can maybe add background colors later controlled by a different prop?
                "text-foreground": color === ColumnColors.None,
                "text-red-800": color === ColumnColors.Red,
                "text-amber-800": color === ColumnColors.Orange,
                "text-green-800": color === ColumnColors.Green,
                "text-blue-800": color === ColumnColors.Blue,
                "text-yellow-800": color === ColumnColors.Yellow,
                "text-indigo-800": color === ColumnColors.Indigo,
                "text-purple-800": color === ColumnColors.Purple,
                "text-pink-800": color === ColumnColors.Pink,
            })}
            {...cellProps}
        >
            <div
                className={cn(
                    "px-4 font-normal w-full h-full flex items-center",
                    "group-hover/cell:overflow-visible group-hover/cell:w-auto group-hover/cell:grow group-hover/cell:bg-inherit",
                    {
                        "group-hover/cell:border-r": ellipsisActive,
                        // Handle login-gating CSS
                        "blur-xs pointer-events-none select-none": blurValue,
                    },
                    {
                        "tabular-nums justify-end": isNumeric,
                    },
                    {
                        "py-0": size === RowSizes.extraSmall,
                        "py-1.5": size === RowSizes.small,
                        "py-3": size === RowSizes.medium,
                        "py-6": size === RowSizes.large,
                        "py-8": size === RowSizes.extraLarge,
                    },
                    {
                        "text-xs": size === RowSizes.extraSmall,
                        "text-sm": size === RowSizes.small,
                        "text-base": size === RowSizes.medium,
                        "text-xl": size === RowSizes.large,
                        "text-2xl": size === RowSizes.extraLarge,
                    },
                    {
                        "px-2 py-1 justify-center":
                            column.type === ColumnTypes.sparkline,
                    },
                )}
            >
                <div
                    ref={textOverflowRef}
                    className={cn("w-full overflow-hidden text-ellipsis", {
                        "flex justify-end": isNumeric,
                    })}
                >
                    {blurValue && (
                        <div className="flex text-xs">
                            {isContentProGated
                                ? "Upgrade to Unlock"
                                : "Login to Unlock"}
                        </div>
                    )}

                    {!blurValue && (
                        <>
                            {column.render !== undefined &&
                                column.render(row, {
                                    size,
                                    width: column.width,
                                    renderOptions: column.renderOptions,
                                })}
                            {column.render === undefined && (
                                <DataGridCellValue
                                    size={size}
                                    type={column.type}
                                    value={column.cellValue(row)}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </td>
    );

    if (!isContentLoginGated) {
        return renderCell();
    }

    return (
        <Popover
            open={showLoginGatePopover}
            onOpenChange={setShowLoginGatePopover}
        >
            <PopoverTrigger asChild>
                {renderCell({
                    onMouseEnter: () => setShowLoginGatePopover(true),
                    onMouseLeave: () => setShowLoginGatePopover(false),
                    onFocus: () => setShowLoginGatePopover(true),
                    onBlur: () => setShowLoginGatePopover(false),
                })}
            </PopoverTrigger>
            <PopoverContent
                side="right"
                align="center"
                sideOffset={6}
                onOpenAutoFocus={(event) => event.preventDefault()}
                className="w-auto border-slate-700 bg-slate-800 px-2 py-1 text-xs text-white shadow-lg"
            >
                Create an account to view
            </PopoverContent>
        </Popover>
    );
}

// // // //

export const DataGridCell = React.memo(DataGridCellContent) as <
    RowType,
    ColumnTypes,
>(
    props: Props<RowType, ColumnTypes>,
) => JSX.Element;
